import { Download } from "lucide-react";
import { LegalLayout, LegalSection } from "@/components/LegalLayout";

const PDF = "/dpa/databehandleraftale-pointtaken.pdf";

function Bullets({ items }) {
  return <ul className="list-disc pl-5 space-y-1.5">{items.map((i) => <li key={i}>{i}</li>)}</ul>;
}

const SUBS = [
  ["OpenAI", "AI-generering og analyser"],
  ["Emergent", "Drift, hosting og udvikling af platformen"],
  ["MongoDB", "Database og brugeradministration"],
  ["Stripe", "Betaling og abonnementer"],
  ["Resend", "Udsendelse af e-mails og notifikationer"],
  ["Mailchimp", "Nyhedsbreve og e-mailmarkedsføring"],
  ["Google Analytics 4", "Statistik, inkl. server-til-server-hændelser (Measurement Protocol) (kun ved samtykke)"],
  ["Microsoft Clarity", "Adfærdsanalyse, session-optagelser og heatmaps (kun ved samtykke)"],
];

export default function DPAPage() {
  return (
    <LegalLayout eyebrow="Databehandleraftale" title="Databehandleraftale (DPA)" updated="juni 2026">
        <a href={PDF} download="Databehandleraftale-PointTaken.pdf"
          className="inline-flex items-center gap-2 rounded-full bg-foreground text-background px-5 py-2.5 text-sm font-medium hover:opacity-90 transition-opacity"
          data-testid="download-dpa-btn">
          <Download className="h-4 w-4" /> Download databehandleraftale (PDF)
        </a>

        <p className="mt-6 text-muted-foreground leading-relaxed">
          Denne databehandleraftale ("Aftalen") regulerer behandlingen af personoplysninger i forbindelse med kundens anvendelse af Aicom AI. Aftalen er indgået mellem:
        </p>
        <div className="mt-4 rounded-2xl border border-border bg-card p-5 text-foreground leading-relaxed">
          <p className="font-medium">Den dataansvarlige (Kunden)</p>
          <p className="text-muted-foreground my-2">og</p>
          <p className="font-medium">Databehandleren</p>
          <p className="text-muted-foreground">
            PointTaken I/S<br />
            CVR: 38540807<br />
            E-mail: <a href="mailto:kontakt@pointtaken.dk" className="underline underline-offset-4 hover:no-underline">kontakt@pointtaken.dk</a>
          </p>
        </div>

        <LegalSection title="1. Formål">
          <p>Denne aftale regulerer PointTakens behandling af personoplysninger på vegne af kunden i forbindelse med levering af Aicom AI og øvrige tilknyttede ydelser. PointTaken behandler alene personoplysninger efter instruks fra kunden og i overensstemmelse med gældende databeskyttelseslovgivning.</p>
        </LegalSection>

        <LegalSection title="2. Behandlingens karakter">
          <p>Aicom AI anvendes til blandt andet:</p>
          <Bullets items={["kommunikationsanalyse", "AI-genereret tekst", "brandfortællinger", "SEO-analyser", "kampagneudvikling", "pressevurderinger", "nyhedsbreve", "LinkedIn-opslag", "upload af dokumenter", "analyse af virksomhedsmateriale"]} />
          <p>Behandlingen består blandt andet af: registrering, strukturering, lagring, analyse, AI-generering, organisering og sletning.</p>
        </LegalSection>

        <LegalSection title="3. Typer af personoplysninger">
          <p>Kunden bestemmer selv hvilke oplysninger, der uploades. Det kan blandt andet være: navne, e-mailadresser, telefonnumre, stillingsbetegnelser, virksomhedsoplysninger, dokumenter, PDF'er, billeder, præsentationer, tekstmateriale og kommunikationsmateriale.</p>
        </LegalSection>

        <LegalSection title="4. Kategorier af registrerede">
          <p>Behandlingen kan omfatte oplysninger om eksempelvis: medarbejdere, kunder, samarbejdspartnere, leverandører, kontaktpersoner, ansøgere og øvrige personer, som kunden vælger at registrere.</p>
        </LegalSection>

        <LegalSection title="5. PointTakens forpligtelser">
          <p>PointTaken forpligter sig til at:</p>
          <Bullets items={[
            "behandle oplysninger fortroligt",
            "kun behandle oplysninger efter kundens instruks",
            "træffe passende tekniske og organisatoriske sikkerhedsforanstaltninger",
            "sikre, at medarbejdere er underlagt tavshedspligt",
            "bistå kunden ved forespørgsler fra registrerede",
            "bistå ved eventuelle sikkerhedsbrud",
            "slette data ved aftalens ophør, medmindre lovgivningen kræver opbevaring",
          ]} />
        </LegalSection>

        <LegalSection title="6. Kundens ansvar">
          <p>Kunden er dataansvarlig og er ansvarlig for:</p>
          <Bullets items={[
            "at have lovligt behandlingsgrundlag",
            "at give nødvendige informationer til registrerede",
            "at sikre lovligheden af de oplysninger, der uploades",
          ]} />
          <p className="text-foreground font-medium mt-4">Følsomme personoplysninger</p>
          <p>Aicom AI er ikke designet eller beregnet til behandling af følsomme personoplysninger (særlige kategorier af personoplysninger, jf. GDPR art. 9 og 10 – fx helbred, CPR-numre, race, religion, politisk overbevisning eller strafbare forhold). Kunden må ikke uploade eller indtaste sådanne oplysninger i tjenesten.</p>
          <p>Uploader eller indtaster kunden alligevel følsomme personoplysninger, sker det udelukkende på kundens eget ansvar og risiko. Kunden indestår i så fald for at have både et behandlingsgrundlag efter art. 6 og en gyldig undtagelse efter art. 9/art. 10. PointTaken påtager sig intet ansvar for følsomme personoplysninger, som kunden i strid med dette uploader til tjenesten.</p>
        </LegalSection>

        <LegalSection title="7. Underdatabehandlere">
          <p>PointTaken anvender følgende underdatabehandlere til levering af Aicom AI:</p>
          <div className="rounded-2xl border border-border overflow-hidden mt-2">
            <div className="grid grid-cols-[minmax(120px,1fr)_2fr] bg-secondary/60 text-foreground text-sm font-medium">
              <div className="px-4 py-3 border-b border-border">Leverandør</div>
              <div className="px-4 py-3 border-b border-border">Formål</div>
            </div>
            {SUBS.map(([name, purpose], i) => (
              <div key={name} className={`grid grid-cols-[minmax(120px,1fr)_2fr] text-sm ${i < SUBS.length - 1 ? "border-b border-border" : ""}`}>
                <div className="px-4 py-3 text-foreground font-medium">{name}</div>
                <div className="px-4 py-3 text-muted-foreground">{purpose}</div>
              </div>
            ))}
          </div>
          <p>PointTaken kan løbende udskifte eller tilføje underdatabehandlere. Kunder orienteres om væsentlige ændringer.</p>
        </LegalSection>

        <LegalSection title="8. Overførsel til tredjelande">
          <p>Visse underdatabehandlere behandler personoplysninger uden for EU/EØS (bl.a. i USA og Indien), herunder vores hosting-/infrastrukturleverandør Emergent Labs Inc. samt OpenAI, Stripe, Google (Analytics) og Microsoft (Clarity). Overførslen sker på et lovligt grundlag: Emergent har indgået en databehandleraftale med os, der inkorporerer EU-Kommissionens standardkontraktbestemmelser (SCC), jf. Kommissionens gennemførelsesafgørelse (EU) 2021/914. Google og Microsoft er certificeret under EU-U.S. Data Privacy Framework. Den aktuelle liste over Emergents underdatabehandlere findes på app.emergent.sh/subprocessors.</p>
        </LegalSection>

        <LegalSection title="9. Informationssikkerhed">
          <p>PointTaken arbejder løbende med informationssikkerhed. Vi anvender blandt andet:</p>
          <Bullets items={["krypterede forbindelser (HTTPS/TLS)", "adgangsstyring", "rollebaserede rettigheder", "sikre cloud-løsninger", "regelmæssig krypteret backup via hostingleverandør", "logning", "løbende softwareopdateringer"]} />
          <p>Vores underliggende hostinginfrastruktur er ISO 27001- (2013 og 2022) og SOC 2 (SSAE 18)-certificeret. Databasen kører på MongoDB Atlas med automatisk krypterede backups.</p>
        </LegalSection>

        <LegalSection title="10. Brud på persondatasikkerheden">
          <p>Ved konstaterede sikkerhedsbrud, der påvirker kundens oplysninger, underretter PointTaken kunden uden unødig forsinkelse.</p>
        </LegalSection>

        <LegalSection title="11. Revision">
          <p>Kunden kan anmode om dokumentation for PointTakens overholdelse af denne aftale. PointTaken kan fremlægge relevant dokumentation eller erklæringer fra underleverandører.</p>
        </LegalSection>

        <LegalSection title="12. Aftalens ophør">
          <p>Ved ophør af samarbejdet slettes personoplysninger, medmindre lovgivningen kræver opbevaring. Vores hostingleverandør sletter eller anonymiserer data senest 45 dage efter kontoens ophør. Data i rutinemæssige backups isoleres og sættes ud af brug, indtil de slettes i henhold til hostingleverandørens backup-cyklus.</p>
        </LegalSection>

        <LegalSection title="13. Kontakt">
          <p>Har du spørgsmål vedrørende databehandling eller denne databehandleraftale, kan du kontakte:</p>
          <p className="text-foreground">
            PointTaken I/S<br />
            E-mail: <a href="mailto:kontakt@pointtaken.dk" className="underline underline-offset-4 hover:no-underline">kontakt@pointtaken.dk</a>
          </p>
          <p>Du kan til enhver tid hente dine uploadede dokumenter direkte i Vidensbanken. Ønsker du at få din øvrige kontodata udleveret eller din konto slettet, kan du skrive til kontakt@pointtaken.dk.</p>
        </LegalSection>
    </LegalLayout>
  );
}
